import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { isolateStaff, selectAccessToken } from '../../../slices/staffSlice';
import { fetchRequests } from '../../../Requests/getRequests';
import { authStaff, getDateAndTime } from "../../../utils/constants";
import { show } from '../../../slices/toastSlice';
import { HugeiconsIcon } from '@hugeicons/react';
import { MoreHorizontalCircle01Icon, EyeIcon, CancelCircleIcon } from '@hugeicons/core-free-icons';
import { CheckmarkCircle01Icon } from '@hugeicons/core-free-icons';

function Debtor({ item, setClientList }) {

    const dispatch = useDispatch();
    const accessToken = useSelector(selectAccessToken);
    const { date, time } = getDateAndTime(item?.createdAt);

    const [dropOpen, setDropOpen] = useState(false);
    const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
    const [processing, setProcessing] = useState(false);

    const btnRef = useRef(null);
    const panelRef = useRef(null);

    const close = useCallback(() => setDropOpen(false), []);

    useEffect(() => {
        if (!dropOpen) return;
        const handler = (e) => {
            if (
                panelRef.current && !panelRef.current.contains(e.target) &&
                btnRef.current && !btnRef.current.contains(e.target)
            ) close();
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [dropOpen, close]);

    useEffect(() => {
        if (!dropOpen) return;
        window.addEventListener("scroll", close, true);
        return () => window.removeEventListener("scroll", close, true);
    }, [dropOpen, close]);

    const openDrop = () => {
        const rect = btnRef.current.getBoundingClientRect();
        const panelW = 160;
        const left = rect.right - panelW < 0 ? rect.left : rect.right - panelW;
        setDropPos({ top: rect.bottom + 6, left });
        setDropOpen(o => !o);
    };

    const passUserId = () => {
        dispatch(isolateStaff(item?._id));
    };

    const handleBanToggle = async () => {
        close();
        setProcessing(true);
        const res = await fetchRequests(`/auth/staff/client/disable`, "PUT", authStaff(accessToken), {}, { _id: item?._id });
        setProcessing(false);
        if (res?.err) {
            dispatch(show({ type: "error", state: true, message: res.err }));
            return;
        }
        dispatch(show({ type: "success", state: true, message: res?.message }));
        if (res?.clients) setClientList(res.clients);
    };

    return (
        <>
        <tr className="hover:bg-gray-50/40 dark:hover:bg-gray-700/30 transition-colors text-sm">
            <td className="px-5 py-3.5">
                <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800 dark:text-gray-100">{item?.otherNames} {item?.surname}</span>
                    {item?.isVerified && (
                        <HugeiconsIcon icon={CheckmarkCircle01Icon} size={14} className="text-blue-400 shrink-0" />
                    )}
                    {!item?.isVerified && item?.verificationStatus === 'pending' && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-100 rounded-full">
                            Pending review
                        </span>
                    )}
                    {item?.isDisabled && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-50 text-red-600 border border-red-100 rounded-full">
                            Banned
                        </span>
                    )}
                </div>
            </td>
            <td className="px-5 py-3.5 text-gray-500 dark:text-gray-400">{item?.email}</td>
            <td className="px-5 py-3.5 text-gray-500 dark:text-gray-400">{item?.phone}</td>
            <td className="px-5 py-3.5 text-gray-500 dark:text-gray-400">{date}, {time}</td>
            <td className="px-5 py-3.5">
                <button
                    ref={btnRef}
                    onClick={openDrop}
                    disabled={processing}
                    className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-200 transition-colors disabled:opacity-40"
                >
                    <HugeiconsIcon icon={MoreHorizontalCircle01Icon} size={20} />
                </button>
            </td>
        </tr>

        {dropOpen && (
            <tr style={{ display: 'contents' }}>
                <td style={{ display: 'contents' }}>
                    <div
                        ref={panelRef}
                        style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999, width: 160 }}
                        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 py-1.5 overflow-hidden"
                    >
                        <Link
                            to="/admin/clients/edit"
                            onClick={passUserId}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <HugeiconsIcon icon={EyeIcon} size={15} className="text-gray-400 shrink-0" />
                            View
                        </Link>
                        <button
                            type="button"
                            onClick={handleBanToggle}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${item?.isDisabled ? 'text-emerald-600' : 'text-red-500'}`}
                        >
                            <HugeiconsIcon icon={CancelCircleIcon} size={15} className="shrink-0" />
                            {item?.isDisabled ? 'Unban' : 'Ban'}
                        </button>
                    </div>
                </td>
            </tr>
        )}
        </>
    );
}

export default Debtor;
